import {ads_config, selectors} from "./config"
import i18next from 'i18next';
import {getCitiesFromIndexDB, saveCitiesToIndexDB} from "./storage";
import {fetchRareData, getAds, getBotsAndConnections, getCitiesFromDjango, processRareData} from "./api";
import {
    adsCategories,
    displayAds,
    displayAdsCount,
    displayAdsCountByStatus,
    handleCheckboxes,
    updateOrphanedAlert,
    updateSyncErrorAlert
} from "./dom-manipulation";
import {setBotsIdsFilter} from "./virtualselect";
import {bindCheckboxes} from "./events";
import urls from "../../common/js/urls";

export const STATUS_GROUPS = {
    active: ["active", "accepted", "published"],
    waiting: ["unconfirmed", "new"],
    unpaid: ["unpaid", "limited"],
    inactive: ["outdated", "removed_by_user", "archived", "archive", "deleted"],
    rejected: ["moderated", "blocked", "disabled", "removed_by_moderator", "removed"],
    orphaned: ["orphaned"],
    other_status: ["other_status"],
};

const KNOWN_OLX_STATUSES = new Set([
    ...STATUS_GROUPS.active,
    ...STATUS_GROUPS.waiting,
    ...STATUS_GROUPS.unpaid,
    ...STATUS_GROUPS.inactive,
    ...STATUS_GROUPS.rejected,
]);

export function normalizeAdStatus(status) {
    if (!status) {
        return 'other_status';
    }

    if (STATUS_GROUPS.active.includes(status)) {
        return 'active';
    }
    if (STATUS_GROUPS.waiting.includes(status)) {
        return 'waiting';
    }
    if (STATUS_GROUPS.unpaid.includes(status)) {
        return 'unpaid';
    }
    if (STATUS_GROUPS.inactive.includes(status)) {
        return 'inactive';
    }
    if (STATUS_GROUPS.rejected.includes(status)) {
        return 'rejected';
    }

    return 'other_status';
}

export function hasUnknownStatuses(ads) {
    return ads.some(ad => !ad.is_orphaned && ad.olx_status && !KNOWN_OLX_STATUSES.has(ad.olx_status));
}


export function countStatuses(statuses, objects) {
    const counts = {};
    const statusSets = {};

    // Initialize counts without mutating the original status definitions.
    for (const category in statuses) {
        if (statuses.hasOwnProperty(category)) {
            counts[category] = 0;
            statusSets[category] = new Set([category, ...statuses[category]]);
        }
    }

    // Count each object status
    for (const object of objects) {
        for (const category in statusSets) {
            if (statusSets.hasOwnProperty(category)) {
                if (statusSets[category].has(object.status)) {
                    counts[category]++;
                    break;
                }
            }
        }
    }

    return counts;
}

const STATUS_TAB_PRIORITY = [
    'active',
    'waiting',
    'unpaid',
    'inactive',
    'rejected',
    'orphaned',
    'other_status',
];

function createEmptyStatusCounts() {
    return Object.keys(STATUS_GROUPS).reduce((acc, key) => {
        acc[key] = 0;
        return acc;
    }, {});
}

export function ensureRenderableStatusTab(tabVisibilityState) {
    if (!tabVisibilityState) {
        return false;
    }

    const currentStatus = $('input[name=tab-btn]:checked').val();
    if (currentStatus !== 'other_status' && currentStatus !== 'orphaned') {
        return false;
    }

    const {counts = createEmptyStatusCounts(), showOrphanedTab = false, showOtherStatusTab = false} = tabVisibilityState;
    if (currentStatus === 'orphaned' && showOrphanedTab) {
        return false;
    }
    if (currentStatus === 'other_status' && showOtherStatusTab) {
        return false;
    }

    const nextStatus = STATUS_TAB_PRIORITY.find(status => {
        if (status === 'orphaned' && !showOrphanedTab) {
            return false;
        }
        if (status === 'other_status' && !showOtherStatusTab) {
            return false;
        }

        return counts[status] > 0;
    });
    if (!nextStatus || nextStatus === currentStatus) {
        return false;
    }

    $(`input[name=tab-btn][value='${nextStatus}']`).prop('checked', true);
    return true;
}

function shouldIncludeAd(ad, key, filterState) {
    const selected = Array.isArray(filterState.selected)
        ? filterState.selected.map(String)
        : String(filterState.selected || "")
            .split(",")
            .map(item => item.trim())
            .filter(Boolean);

    if (filterState.isAll || selected.length === 0) {
        return true;
    }

    const adValue = ad && ad[key] !== undefined && ad[key] !== null ? ad[key].toString() : "";
    return selected.includes(adValue);
}

function botPresenceFilter(ad, filterState) {
    if (filterState.isAll) {
        return true;
    }

    const selected = Array.isArray(filterState.selected)
        ? filterState.selected.map(String)
        : [];

    const hasBot = !!ad.bot_id;
    if (selected.includes("withoutBot")) {
        return !hasBot;
    }

    if (selected.includes("withBot")) {
        return hasBot;
    }

    return true;
}

function getStatusFilterState(statusOverride = undefined) {
    const statusFilter = statusOverride === undefined
        ? $('input[name=tab-btn]:checked').val()
        : statusOverride;
    const isAll = false;
    const selected = statusFilter ? [statusFilter] : [];
    return {selected, isAll}
}

function normalizeSelectedOptions(selectedOptionsRaw) {
    if (!selectedOptionsRaw) {
        return [];
    }

    if (Array.isArray(selectedOptionsRaw)) {
        return selectedOptionsRaw;
    }

    if (typeof selectedOptionsRaw === 'object' && selectedOptionsRaw.value !== undefined) {
        return [selectedOptionsRaw];
    }

    if (typeof selectedOptionsRaw[Symbol.iterator] === 'function') {
        return Array.from(selectedOptionsRaw);
    }

    if (typeof selectedOptionsRaw === 'object') {
        return Object.values(selectedOptionsRaw);
    }

    return [selectedOptionsRaw];
}

function getFilterState(querySelector) {
    const element = document.querySelector(querySelector);
    let selected = [];

    if (element && typeof element.getSelectedOptions === 'function') {
        const selectedOptionsRaw = element.getSelectedOptions();
        const selectedOptions = normalizeSelectedOptions(selectedOptionsRaw);
        selected = selectedOptions
            .map(option => {
                if (option && typeof option === 'object' && option.value !== undefined && option.value !== null) {
                    return String(option.value).trim();
                }

                return String(option || "").trim();
            })
            .filter(Boolean);
    } else {
        const selectedRaw = element ? element.value : [];
        selected = Array.isArray(selectedRaw)
            ? selectedRaw.map(item => String(item).trim()).filter(Boolean)
            : String(selectedRaw || "")
                .split(",")
                .map(item => item.trim())
                .filter(Boolean);
    }

    const isAll = Boolean(element && typeof element.isAllSelected === 'function' && element.isAllSelected()) ||
        selected.includes("all");

    if (querySelector === selectors.accounts) {
        console.log('accounts filter state', {selectedOptionsRaw: element && typeof element.getSelectedOptions === 'function' ? element.getSelectedOptions() : null, selected, isAll});
    }

    return {selected, isAll};
}

export function superFilter(ads, statusOverride = undefined) {
    if (!ads || ads.length === 0) {
        return ads;
    }

    const botPresenceFilterState = getFilterState(selectors.botPresenceFilter);
    const botIdsFilterState = getFilterState(selectors.botIdsFilter);
    const categoriesFilterState = getFilterState(selectors.categories);
    const statusFilterState = getStatusFilterState(statusOverride);
    const isOrphanedTab = Array.isArray(statusFilterState.selected) && statusFilterState.selected.includes('orphaned');

    return ads.filter(ad => {
        return shouldIncludeAd(ad, 'status', statusFilterState) &&
            shouldIncludeAd(ad, 'bot_id', botIdsFilterState) &&
            botPresenceFilter(ad, botPresenceFilterState) &&
            (isOrphanedTab || shouldIncludeAd(ad, 'category_id', categoriesFilterState))

    });


}

function filterAdsByAccount(ads) {
    if (!ads || ads.length === 0) {
        return ads;
    }
    const accountFilterState = getFilterState(selectors.accounts);
    return ads.filter(ad => {
        return matchesAccountFilter(ad, accountFilterState)
    });

}

export function getScopedAdsForStatus(ads, statusOverride = undefined) {
    if (!ads || ads.length === 0) {
        return ads;
    }

    const filteredAdsByAccount = filterAdsByAccount(ads);
    const filteredAds = superFilter(filteredAdsByAccount, statusOverride);
    return searchAds(filteredAds);
}

export function getScopedAdsForTabVisibility(ads) {
    if (!ads || ads.length === 0) {
        return ads;
    }

    const filteredAdsByAccount = filterAdsByAccount(ads);
    const botPresenceFilterState = getFilterState(selectors.botPresenceFilter);
    const botIdsFilterState = getFilterState(selectors.botIdsFilter);
    const filteredAds = filteredAdsByAccount.filter(ad => {
        return shouldIncludeAd(ad, 'bot_id', botIdsFilterState) &&
            botPresenceFilter(ad, botPresenceFilterState);
    });
    return searchAds(filteredAds);
}

export function getStatusTabVisibilityState(ads) {
    const scopedAds = getScopedAdsForTabVisibility(ads);
    const counts = Array.isArray(scopedAds) && scopedAds.length > 0
        ? countStatuses({...STATUS_GROUPS}, scopedAds)
        : createEmptyStatusCounts();

    counts.orphaned = Array.isArray(scopedAds)
        ? scopedAds.filter(ad => ad.is_orphaned || ad.status === 'orphaned').length
        : 0;
    counts.other_status = Array.isArray(scopedAds)
        ? scopedAds.filter(ad => !ad.is_orphaned && ad.status === 'other_status').length
        : 0;

    return {
        ads: scopedAds || [],
        counts,
        showOrphanedTab: counts.orphaned > 0,
        showOtherStatusTab: counts.other_status > 0,
    };
}

function filterAdsByStatus(ads) {
    if (!ads || ads.length === 0) {
        return ads;
    }

    const status = getStatusFilterState().selected;

    return ads.filter(ad => status.includes(ad.status));
}

export function createCategoryTree(leafCategoryId) {
    const categories = ads_config.rareData.categories.data;

    const leafCategory = categories.find(category => category.id === leafCategoryId);
    if (!leafCategory) {
        return '';
    }

    const getCategoryAncestors = (categoryId, ancestors = []) => {
        const category = categories.find(category => category.id === categoryId);
        if (!category) {
            return ancestors;
        }
        ancestors.unshift(category);
        if (category.parent_id) {
            getCategoryAncestors(category.parent_id, ancestors);
        }
        return ancestors;
    }

    const ancestors = getCategoryAncestors(leafCategoryId);
    const listItems = ancestors.map(category => {
        return `<li>${category.name}</li>`;
    });

    return `${listItems.join('')}`;
}

// Function to get unique city IDs from an array of ads
function getUniqueCityIds(ads) {
    const cityIds = ads
        .map(ad => ad.location && ad.location.city_id)
        .filter(Boolean);
    return [...new Set(cityIds)];
}

// Main function to get unique city IDs from an array of ads, check if they are in indexDB storage, and get new cities from Django API if necessary
export async function getCitiesFromAds(ads) {
    try {
        const cityIds = getUniqueCityIds(ads);
        let citiesFromIndexDB = await getCitiesFromIndexDB(cityIds);

        console.log('ads', ads);
        console.log('cityIds', cityIds);
        console.log('ads_config.cityIdsToUpdate', ads_config.cityIdsToUpdate);

        let citiesFromDjango = [];
        if (ads_config.cityIdsToUpdate.length > 0) {
            console.log('cityIdsToUpdate.length > 0');
            citiesFromDjango = await getCitiesFromDjango(ads_config.cityIdsToUpdate);
            console.log('cityIdsToUpdate.length > 0 citiesFromDjango', citiesFromDjango);
        }

        console.log('citiesFromIndexDB', citiesFromIndexDB);
        console.log('citiesFromDjango', citiesFromDjango);

        // Save new cities to indexDB
        if (citiesFromDjango.length > 0) {
            await saveCitiesToIndexDB(citiesFromDjango);
        }

        return [...citiesFromIndexDB, ...citiesFromDjango];
    } catch (error) {
        console.error('Error occurred while getting cities from ads:', error);
    }
}

export function connectedBots(connections, bots) {
    const botIdsInConnectionsSet = new Set(
        connections.filter(c => c.bot_id !== '').map(c => c.bot_id)
    );
    return bots.filter(bot => botIdsInConnectionsSet.has(bot.id));
}

function normalizeAccountId(value) {
    if (value === undefined || value === null || value === "") {
        return "";
    }

    return String(value);
}

function getCanonicalAccountId(account) {
    if (!account || typeof account !== "object") {
        return "";
    }

    return normalizeAccountId(account.id || account.account_id);
}

function getAccountCandidateIds(account) {
    if (!account || typeof account !== "object") {
        return [];
    }

    return [
        getCanonicalAccountId(account),
        normalizeAccountId(account.id),
        normalizeAccountId(account.account_id),
    ].filter(Boolean);
}

function getAccountById(accountId) {
    const normalizedAccountId = normalizeAccountId(accountId);

    if (!normalizedAccountId) {
        return null;
    }

    return ads_config.accounts.find(account => {
        return getAccountCandidateIds(account).includes(normalizedAccountId);
    }) || null;
}

function getAdAccountCandidateIds(ad) {
    if (!ad || typeof ad !== "object") {
        return [];
    }

    const candidateIds = [
        normalizeAccountId(ad.account_id),
        normalizeAccountId(ad.raw_account_id),
    ];

    const resolvedAccount = getAccountById(ad.account_id) || getAccountById(ad.raw_account_id);
    if (resolvedAccount) {
        candidateIds.push(...getAccountCandidateIds(resolvedAccount));
    }

    return [...new Set(candidateIds.filter(Boolean))];
}

function matchesAccountFilter(ad, filterState) {
    const selected = Array.isArray(filterState.selected)
        ? filterState.selected.map(item => String(item).trim()).filter(Boolean)
        : [];

    if (filterState.isAll || selected.length === 0) {
        return true;
    }

    const candidateIds = getAdAccountCandidateIds(ad);
    return selected.some(selectedId => candidateIds.includes(selectedId));
}

function buildOrphanedAd(connection, botsData) {
    const bot = botsData.find(item => String(item.id) === String(connection.bot_id));
    const account = getAccountById(connection.account_id);
    const botName = connection.bot_name || (bot ? bot.name : "");
    const rawAccountId = normalizeAccountId(connection.account_id);
    const canonicalAccountId = account
        ? getCanonicalAccountId(account)
        : rawAccountId;

    if (!canonicalAccountId) {
        console.warn('Orphaned ad is missing canonical account id', {
            connection_id: connection.connection_id,
            ad_id: connection.ad_id,
            raw_account_id: connection.account_id,
        });
    }

    return {
        id: connection.ad_id,
        title: connection.ad_title || i18next.t('ads:dom.orphanedFallbackTitle', {ad_id: connection.ad_id}),
        status: 'orphaned',
        olx_status: '',
        is_orphaned: true,
        bot_id: bot ? bot.id : connection.bot_id,
        bot_name: botName,
        connection_id: connection.connection_id,
        connected_to: connection.connected_to,
        valid_to: connection.valid_to,
        activated_at: "",
        account_id: canonicalAccountId,
        raw_account_id: rawAccountId,
        account_name: account ? (account.name || account.account_name || "") : "",
        account_email: account ? (account.email || account.account_email || "") : "",
        category_id: null,
        images: connection.ad_photo ? [{url: connection.ad_photo}] : [],
        price: {value: 0, currency: "UAH", ua: 0},
        location_names: {
            city_name: "",
            region_name: "",
            district_name: "",
        },
        location: {
            city_id: null,
            district_id: null,
        },
        url: "",
    };
}

function buildFailedAccountInfo(account, error) {
    const payload = error && error.responseJSON ? error.responseJSON : {};
    const accountId = normalizeAccountId(payload.account_id || account.account_id);
    const accountName = payload.account_name || account.account_name || account.name || "";
    const accountEmail = account.account_email || account.email || "";

    return {
        account_id: accountId,
        account_name: accountName || accountEmail || accountId,
        account_email: accountEmail,
        message: payload.message || "",
        requires_reconnect: Boolean(payload.requires_reconnect),
    };
}

export function mergeAdsWithOrphaned(olxAds, botsData, connectionsData, excludedAccountIds = []) {
    const mergedAds = Array.isArray(olxAds) ? [...olxAds] : [];
    const existingAdIds = new Set(mergedAds.map(ad => String(ad.id)));
    const excludedAccountIdsSet = new Set((Array.isArray(excludedAccountIds) ? excludedAccountIds : [])
        .map(accountId => normalizeAccountId(accountId))
        .filter(Boolean));

    const orphanedAds = connectionsData
        .filter(connection => {
            const connectionAccountId = normalizeAccountId(connection.account_id);
            return connection.bot_id !== '' &&
                connection.ad_id &&
                !existingAdIds.has(String(connection.ad_id)) &&
                !excludedAccountIdsSet.has(connectionAccountId);
        })
        .map(connection => buildOrphanedAd(connection, botsData));

    return [...mergedAds, ...orphanedAds];
}

function getLocationNames(location) {
    let district;
    let locationNames = {};
    let city = {}
    try {
        city = ads_config.all_cities.find(elem => elem.id === location.city_id);
    } catch (error) {
        console.error(error);
    }

    console.log("location", location)
    console.log("ads_config.all_cities", ads_config.all_cities)
    console.log("city", city)
    console.log("ads_config.rareData.regions", ads_config.rareData.regions)

    if (!city) {
        return {
            city_name: "",
            region_name: "",
            district_name: "",
        };
    }

    const region = ads_config.rareData.regions.data.find(region => region.id === city.region_id);
    locationNames.city_name = city.name || "";
    locationNames.region_name = region ? region.name : "";

    if (location.district_id) {
        district = ads_config.rareData.districts.data.find(district => district.id === location.district_id);
        locationNames.district_name = district ? district.name : "";
    }
    ;

    return locationNames

}

export function searchAds(ads) {
    if (!ads || ads.length === 0) {
        return ads;
    }

    const searchTerm = $("#search").val().toLowerCase();

    if (!searchTerm) {
        return ads;
    }

    return ads.filter(ad =>
        String(ad.title || "").toLowerCase().includes(searchTerm) ||
        String(ad.id || "").includes(searchTerm) ||
        String(ad.account_name || "").toLowerCase().includes(searchTerm) ||
        String(ad.account_id || "").includes(searchTerm) ||
        String(ad.bot_name || "").toLowerCase().includes(searchTerm) ||
        String(ad.bot_id || "").includes(searchTerm));
}

export function processAdsLocations(ads) {
    let arr = [];
    for (let ad_now of ads) {
        try {
            ad_now.location_names = getLocationNames(ad_now.location);
        } catch (error) {
            console.error("Error processing ad location", ad_now && ad_now.id, error);
            ad_now.location_names = {
                city_name: "",
                region_name: "",
                district_name: "",
            };
        }
        arr.push(ad_now)
    }
    ;
    return arr
}

function getBotDetails(ad, botsData, connectionsData) {
    let botDetails = {connection_id: "", bot_name: "", bot_id: ""};

    const connection = connectionsData.find(c => c.ad_id === ad.id);
    if (connection) {
        const bot = botsData.find(b => b.id === connection.bot_id);
        botDetails.connection_id = connection.connection_id;
        botDetails.connected_to = connection.connected_to;
        botDetails.bot_name = bot ? bot.name : "";
        botDetails.bot_id = bot ? bot.id : "";
    }

    return botDetails;
}

export function processAdsBots(adsData, botsData, connectionsData) {
    const all_data = [];

    try {
        for (const account of adsData) {
            const accountAds = Array.isArray(account.ads) ? account.ads : [];
            if (!Array.isArray(account.ads)) {
                console.error("Account ads payload is not an array", account && account.account, account && account.ads);
            }

            for (const ad of accountAds) {
                let newAd = {...ad};

                if (account.account) {
                    newAd.account_email = account.account.account_email;
                    newAd.account_id = getCanonicalAccountId(account.account);
                    newAd.raw_account_id = normalizeAccountId(account.account.account_id || account.account.id);
                    newAd.account_name = account.account.account_name;
                }


                if (!newAd.hasOwnProperty('price') || newAd.price === null) {
                    console.log("!ad.hasOwnProperty")
                    newAd.price = {};
                    newAd.price.ua = 0;
                    newAd.price.value = 0;
                } else {
                    newAd.price['ua'] = convertPriceToUAH(newAd, ads_config.currencies_rates);
                }

                const botDetails = getBotDetails(newAd, botsData, connectionsData);
                newAd.olx_status = newAd.status || "";
                newAd.status = normalizeAdStatus(newAd.status);
                newAd.connection_id = botDetails.connection_id;
                newAd.connected_to = botDetails.connected_to;
                newAd.bot_name = botDetails.bot_name;
                newAd.bot_id = botDetails.bot_id;

                all_data.push(newAd);
            }
        }
    } catch (error) {
        console.error("An error occurred while processing the ads: ", error);
    }
    console.log('processAdsBots', all_data)
    return all_data;
}

export function refreshAdsBotConnections(ads, botsData, connectionsData) {
    if (!Array.isArray(ads)) {
        return [];
    }

    return ads.map(ad => {
        const botDetails = getBotDetails(ad, botsData, connectionsData);
        return {
            ...ad,
            connection_id: botDetails.connection_id,
            connected_to: botDetails.connected_to,
            bot_name: botDetails.bot_name,
            bot_id: botDetails.bot_id,
        };
    });
}


function safeAssignPrice(ad, ads_config) {
    // Проверяем, есть ли поле price, если нет - создаем его
    if (!ad.hasOwnProperty('price')) {
        ad.price = {};
    }

    // Выполняем присвоение
    ad.price['ua'] = convertPriceToUAH(ad, ads_config.currencies_rates);
}


// Function to convert the ad's price to Ukrainian Hryvnia (UAH) if needed
function convertPriceToUAH(ad, rates) {
    let priceUA = 0;

    if (ad.price && ad.price.value > 0) {

        if (ad.price.currency !== 'UAH' && rates[ad.price.currency]) {
            priceUA = Math.round(ad.price.value * rates[ad.price.currency]);
        } else {
            priceUA = ad.price.value;
        }
    }

    return priceUA;
}

function byFieldAsc(field) {
    return (a, b) => a[field] === b[field] ? 0 : (a[field] > b[field] ? 1 : -1);
}

function byFieldDsc(field) {
    return (a, b) => a[field] === b[field] ? 0 : (a[field] > b[field] ? -1 : 1);
}

function byNumberFieldAsc(field1, field2) {
    return (a, b) => {
        const valueA = parseInt(a[field1][field2]);
        const valueB = parseInt(b[field1][field2]);

        if (valueA === valueB) {
            return 0;
        } else {
            return valueA > valueB ? 1 : -1;
        }
    };
}

function byNumberFieldDsc(field1, field2) {
    return (a, b) => {
        const valueA = parseInt(a[field1][field2]);
        const valueB = parseInt(b[field1][field2]);

        if (valueA === valueB) {
            return 0;
        } else {
            return valueA > valueB ? -1 : 1;
        }
    };
}

function byDateFieldAsc(field) {
    return (a, b) => {
        const valueA = new Date(a[field]);
        const valueB = new Date(b[field]);
        if (valueA.getTime() === valueB.getTime()) {
            return 0;
        } else {
            return valueA > valueB ? 1 : -1;
        }
    };
}

function byDateFieldDsc(field) {
    return (a, b) => {
        const dateA = new Date(a[field]);
        const dateB = new Date(b[field]);
        if (dateA.getTime() === dateB.getTime()) {
            return 0;
        } else {
            return dateA > dateB ? -1 : 1;
        }
    };
}


export function sortAds(ads) {
    let sortedAds = [];
    let virtualSelect = document.querySelector('#sorting');
    const selected = virtualSelect.getSelectedOptions();
    if (selected) {
        switch (selected.value) {
            case 'title':
                sortedAds = ads.sort(byFieldAsc('title'))
                break;
            case '-title':
                sortedAds = ads.sort(byFieldDsc('title'))
                break;
            case 'activated_at':
                sortedAds = ads.sort(byDateFieldAsc('activated_at'))
                break;
            case '-activated_at':
                sortedAds = ads.sort(byDateFieldDsc('activated_at'))
                break;
            case 'price':
                sortedAds = ads.sort(byNumberFieldAsc('price', 'ua'))
                break;
            case '-price':
                sortedAds = ads.sort(byNumberFieldDsc('price', 'ua'))
                break;
            case 'valid_to':
                sortedAds = ads.sort(byDateFieldAsc('valid_to'))
                break;
            case '-valid_to':
                sortedAds = ads.sort(byDateFieldDsc('valid_to'))
                break;
        }

    } else {
        return ads;
    }


    return sortedAds;


}


export function sortConnections(arr) {
    return arr.sort((a, b) => {
        const aDate = new Date(a.connected_to || a.valid_to);
        const bDate = new Date(b.connected_to || b.valid_to);
        return bDate - aDate;
    });
};

export function paginateAds(ads) {
    const limit = parseInt($('.page-limit-selected').text());
    console.log('paginateAds limit', limit);
    $('#pagination').pagination({
        dataSource: ads,
        pageSize: limit,
        pageRange: 3,
        hideOnlyOnePage: true,
        className: "pagination justify-content-center",
        pageClassName: "page-item",
        prevClassName: "page-item",
        nextClassName: "page-item",
        activeClassName: "active",
        disableClassName: "disabled",
        ulClassName: "pagination",
        prevText: "&laquo;",
        nextText: "&raquo;",
        callback: function (data, pagination) {
            displayAds(data);
        }
    });
}

export function makeAccountsIds(accounts) {
    return $.map(accounts, function (el) {
        return {
            "account_id": el.id,
            "account_email": el.email,
            "account_name": el.name
        };


    });
}

export function processAds(event) {
    console.log('processAds', event);
    const filteredAdsByAccount = filterAdsByAccount(ads_config.all_ads);
    const tabVisibilityState = getStatusTabVisibilityState(ads_config.all_ads);
    updateOrphanedAlert(tabVisibilityState && tabVisibilityState.showOrphanedTab
        ? tabVisibilityState.counts.orphaned
        : 0);

    displayAdsCountByStatus(tabVisibilityState);

    if (ensureRenderableStatusTab(tabVisibilityState)) {
        processAds(event);
        return;
    }

    let filteredAds = superFilter(filteredAdsByAccount);

    filteredAds = searchAds(filteredAds);

    if (!event) {
        setBotsIdsFilter();
        adsCategories(filteredAds, ads_config.rareData.categories.data);
    }

    displayAdsCount(filteredAds.length);

    filteredAds = sortAds(filteredAds);
    paginateAds(filteredAds);
    handleCheckboxes();
    bindCheckboxes();


}

export async function processAccounts(array) {
    let spinner = $('#spinner');
    spinner.show();
    ads_config.olx_ads = [];
    ads_config.all_ads = [];

    try {
        const loadedAds = [];
        const failedAccountIds = [];
        const failedAccounts = [];
        for (const item of array) {
            try {
                const accountAds = await getAds(item);
                loadedAds.push(accountAds);
            } catch (error) {
                console.error("Error while loading ads for account", item && item.account_id, error);
                const failedAccount = buildFailedAccountInfo(item || {}, error);
                failedAccountIds.push(failedAccount.account_id);
                failedAccounts.push(failedAccount);
            }
        }

        updateSyncErrorAlert(failedAccounts);


        await processRareData();

        const rates = await fetchRareData(urls.currencies, 86400);
        ads_config.currencies_rates = rates;

        await getBotsAndConnections();

        ads_config.olx_ads = await processAdsBots(loadedAds, ads_config.all_bots, ads_config.all_connections);

        ads_config.all_cities = await getCitiesFromAds(ads_config.olx_ads);

        ads_config.olx_ads = await processAdsLocations(ads_config.olx_ads);
        ads_config.all_ads = mergeAdsWithOrphaned(
            ads_config.olx_ads,
            ads_config.all_bots,
            ads_config.all_connections,
            failedAccountIds
        );

        processAds();
    } catch (error) {
        console.error(error);
    } finally {
        spinner.hide();
    }
}
